import React, { useEffect, useMemo } from 'react';
import { CCard, CCardBody, CCol, CContainer, CForm, CFormFeedback, CFormInput, CFormLabel, CFormSelect, CRow, CCarousel, CCarouselItem, CTooltip } from '@coreui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { CFormInputWithMask } from 'components/input';
import { CustomButton } from 'components/buttons';
import { InputDatePicker } from 'components/date';
import { UnifiedLoader } from 'components/loader';
import { ToastBase } from 'components/toast';
import { selectLoading } from 'features/loader';
import { fetchPublicGeneros, selectPublicGeneros } from 'features/public/catalogSlice';
import {
  createAlumnoPublic,
  resetRegisterForm,
  resetRegisterData,
  selectRegisterForm,
  selectRegisterStatus,
  setRegisterField,
  checkAvailableDiplomados,
  selectAvailableDiplomados,
  selectHasAvailableDiplomados,
  selectCheckingDiplomados,
} from 'features/public/registerSlice';
import { selectDataToast, selectMessageToast, selectTitleToast, selectTypeToast, selectVisibleToast, setVisibleToast } from 'features/toast';
import { SERVER_ROUTE } from 'utils/constants';
import Icon from 'components/icon/Icon';
import DiplomadoImage from 'components/image/DiplomadoImage';
import './Registro.scss';

const validationSchema = Yup.object().shape({
  numeroTelefono: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^[0-9]{8}$/, 'Debe contener exactamente 8 dígitos')
    .required('Campo requerido'),
  primerNombre: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^[\p{L}']+$/u, 'Solo se permiten letras, sin espacios')
    .max(50, 'Máximo 50 caracteres')
    .test('no-spaces', 'No se permiten espacios. Use un solo nombre por campo', (value) => !value || !/\s/.test(value))
    .required('Campo requerido'),
  segundoNombre: Yup.string()
    .transform((value) => (value ? value.trim().replace(/\s+/g, ' ') : value))
    .matches(/^[\p{L}'\s]*$/u, 'Solo se permiten letras y espacios')
    .max(50, 'Máximo 50 caracteres')
    .test('max-spaces', 'Máximo 3 espacios permitidos', (value) => !value || (value.match(/\s/g) || []).length <= 3)
    .nullable(),
  tercerNombre: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^[\p{L}']*$/u, 'Solo se permiten letras, sin espacios')
    .max(50, 'Máximo 50 caracteres')
    .test('no-spaces', 'No se permiten espacios. Use un solo nombre por campo', (value) => !value || !/\s/.test(value))
    .nullable(),
  primerApellido: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^[\p{L}']+$/u, 'Solo se permiten letras, sin espacios')
    .max(50, 'Máximo 50 caracteres')
    .test('no-spaces', 'No se permiten espacios. Use un solo apellido por campo', (value) => !value || !/\s/.test(value))
    .required('Campo requerido'),
  segundoApellido: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .matches(/^[\p{L}']*$/u, 'Solo se permiten letras, sin espacios')
    .max(50, 'Máximo 50 caracteres')
    .test('no-spaces', 'No se permiten espacios. Use un solo apellido por campo', (value) => !value || !/\s/.test(value))
    .nullable(),
  cui: Yup.string()
    .transform((value) => (value ? value.replace(/\s+/g, '') : value))
    .matches(/^[0-9]*$/, 'Sólo se permiten números')
    .test('len', 'Debe tener exactamente 13 dígitos', (value) => !value || value.length === 13)
    .required('Campo requerido'),
  idGenero: Yup.number().typeError('Campo requerido').required('Campo requerido'),
  fechaNacimiento: Yup.date()
    .nullable()
    .required('Campo requerido')
    .max(new Date(), 'La fecha no puede ser futura')
    .test('min-age', 'Debes tener al menos 5 años', (value) => {
      if (!value) return false;
      const today = new Date();
      const birthDate = new Date(value);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      const finalAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
      return finalAge >= 5;
    })
    .test('max-age', 'La edad no puede ser mayor a 100 años', (value) => {
      if (!value) return false;
      const today = new Date();
      const birthDate = new Date(value);
      const age = today.getFullYear() - birthDate.getFullYear();
      return age <= 100;
    }),
});

const Registro = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loading = useAppSelector(selectLoading);
  const status = useAppSelector(selectRegisterStatus);
  const generos = useAppSelector(selectPublicGeneros);
  const generoOptions = useMemo(() => generos.filter((item: any) => item.estado !== false), [generos]);
  const registerForm = useAppSelector(selectRegisterForm);
  const availableDiplomados = useAppSelector(selectAvailableDiplomados);
  const hasAvailableDiplomados = useAppSelector(selectHasAvailableDiplomados);
  const checkingDiplomados = useAppSelector(selectCheckingDiplomados);

  const visible = useAppSelector(selectVisibleToast);
  const title = useAppSelector(selectTitleToast);
  const message = useAppSelector(selectMessageToast);
  const data = useAppSelector(selectDataToast);
  const color = useAppSelector(selectTypeToast);

  const [showForm, setShowForm] = React.useState(false);

  const formik = useFormik({
    initialValues: registerForm,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const sanitized = {
        numeroTelefono: values.numeroTelefono.trim(),
        primerNombre: values.primerNombre.trim(),
        segundoNombre: values.segundoNombre ? values.segundoNombre.trim() : '',
        tercerNombre: values.tercerNombre ? values.tercerNombre.trim() : '',
        primerApellido: values.primerApellido.trim(),
        segundoApellido: values.segundoApellido ? values.segundoApellido.trim() : '',
        cui: values.cui ? values.cui.replace(/\s+/g, '') : '',
        idGenero: values.idGenero ? Number(values.idGenero) : '',
        fechaNacimiento: values.fechaNacimiento,
      };

      Object.entries(sanitized).forEach(([field, value]) => {
        dispatch(setRegisterField({ field: field as keyof typeof registerForm, value }));
      });

      const resultAction = await dispatch(createAlumnoPublic({ redirectToLogin: true }));
      if (createAlumnoPublic.fulfilled.match(resultAction)) {
        dispatch(resetRegisterForm());
        dispatch(resetRegisterData());
        navigate(`${SERVER_ROUTE}/principal`, { replace: true });
      }
    },
  });

  useEffect(() => {
    dispatch(fetchPublicGeneros());
    dispatch(checkAvailableDiplomados());
    return () => {
      dispatch(resetRegisterForm());
      dispatch(resetRegisterData());
    };
  }, [dispatch]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'segundoNombre') {
      const cleanValue = value.replace(/\s+/g, ' ');
      formik.setFieldValue(name, cleanValue);
    } else {
      const cleanValue = value.replace(/\s/g, '');
      formik.setFieldValue(name, cleanValue);
    }
  };

  if (checkingDiplomados) {
    return (
      <div className="register-page min-vh-100 d-flex align-items-center py-5">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol lg={8} md={10}>
              <CCard className="register-card shadow-lg border-0">
                <CCardBody className="p-5 text-center">
                  <UnifiedLoader size="md" text="Verificando diplomados disponibles..." />
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    );
  }

  if (!hasAvailableDiplomados) {
    return (
      <div className="register-page min-vh-100 d-flex align-items-center py-5">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol lg={8} md={10}>
              <CCard className="empty-state-card shadow-lg border-0 overflow-hidden">
                <div className="empty-state-bg position-absolute w-100 h-100"></div>
                <CCardBody className="p-3 p-lg-4 position-relative">
                  <div className="text-center animate-fade-in">
                    <div className="mb-3 position-relative">
                      <div className="pulse-animation">
                        <div className="rounded-circle bg-gradient-warning mx-auto d-flex align-items-center justify-content-center shadow-lg" style={{ width: '80px', height: '80px' }}>
                          <Icon icono="mdiCalendarClock" color="white" size={2.5} />
                        </div>
                      </div>
                    </div>

                    <h1 className="fw-bold text-dark mb-2" style={{ fontSize: '1.75rem' }}>
                      Inscripciones Cerradas
                    </h1>

                    <div className="mb-3 mx-auto" style={{ maxWidth: '500px' }}>
                      <p className="text-muted mb-2" style={{ fontSize: '0.95rem' }}>
                        En este momento no hay diplomados con inscripciones abiertas
                      </p>
                      <div className="info-alert p-2 rounded-3 mb-2" style={{ background: 'linear-gradient(135deg, #fff9e6 0%, #fff 100%)', border: '2px dashed #ffc107' }}>
                        <Icon icono="mdiInformationOutline" color="warning" size={1} className="me-2" />
                        <span className="text-dark" style={{ fontSize: '0.9rem' }}>Las inscripciones abrirán próximamente</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="row g-2 justify-content-center">
                        <div className="col-md-4">
                          <a
                            href="https://wa.me/50233580367?text=Hola,%20me%20gustaría%20obtener%20información%20sobre%20los%20diplomados"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="suggestion-card p-2 rounded-3 h-100" style={{ background: 'rgba(var(--cui-info-rgb), 0.05)', border: '1px solid rgba(var(--cui-info-rgb), 0.2)', transition: 'all 0.3s ease', cursor: 'pointer' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <Icon icono="mdiWhatsapp" color="success" size={1.5} className="mb-2" />
                              <h6 className="fw-semibold text-dark mb-1" style={{ fontSize: '0.85rem' }}>Escríbenos por WhatsApp</h6>
                              <small className="text-muted" style={{ fontSize: '0.75rem' }}>+502 3358 0367</small>
                            </div>
                          </a>
                        </div>
                        <div className="col-md-4">
                          <a
                            href="mailto:lideresenaccion@genesisempresarial.com?subject=Consulta%20sobre%20diplomados&body=Hola,%20me%20gustaría%20obtener%20información%20sobre%20los%20diplomados."
                            className="text-decoration-none"
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="suggestion-card p-2 rounded-3 h-100" style={{ background: 'rgba(var(--cui-success-rgb), 0.05)', border: '1px solid rgba(var(--cui-success-rgb), 0.2)', transition: 'all 0.3s ease', cursor: 'pointer' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <Icon icono="mdiEmailOutline" color="success" size={1.5} className="mb-2" />
                              <h6 className="fw-semibold text-dark mb-1" style={{ fontSize: '0.85rem' }}>Contacta al equipo</h6>
                              <small className="text-muted" style={{ fontSize: '0.75rem' }}>lideresenaccion@genesisempresarial.com</small>
                            </div>
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-top">
                      <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>¿Ya tienes una cuenta registrada?</p>
                      <CustomButton
                        variant="primary"
                        color="primary"
                        className="px-4 py-2 shadow-sm"
                        onClick={() => navigate(`${SERVER_ROUTE}/principal`)}
                      >
                        <Icon icono="mdiLogin" className="me-2" size={0.9} />
                        Inicia sesión
                      </CustomButton>
                    </div>
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    );
  }

  if (!showForm) {
    const hasMultipleDiplomados = availableDiplomados.length > 1;

    return (
      <div className="register-page min-vh-100 d-flex align-items-center py-3">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol lg={11} xl={10} xxl={9}>
              <div className="text-center mb-3 animate-fade-in">
                <h1 className="fw-bold text-white mb-1" style={{ fontSize: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                  Diplomados Disponibles
                </h1>
                <p className="text-white mb-0" style={{ opacity: 0.85, fontSize: '0.875rem' }}>
                  {hasMultipleDiplomados
                    ? 'Explora nuestros programas con inscripciones abiertas'
                    : 'Descubre nuestro programa disponible'}
                </p>
              </div>

              <div className="carousel-wrapper position-relative">
                <CCard className="shadow-lg border-0 overflow-hidden diplomado-showcase-card">
                  <CCardBody className="p-0">
                    <CCarousel
                      controls={hasMultipleDiplomados}
                      indicators={hasMultipleDiplomados}
                      dark
                      className="diplomado-carousel"
                    >
                      {availableDiplomados.map((diplomado: any) => (
                        <CCarouselItem key={diplomado.idDiplomado}>
                          <div className="diplomado-carousel-item">
                            <CRow className="g-0 h-100">
                              <CCol lg={4} className="diplomado-image-section">
                                <div className="image-wrapper">
                                  <div className="floating-badge">
                                    <span className="badge bg-success shadow-sm px-3 py-2 rounded-pill" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                      <Icon icono="mdiCheckCircle" size={0.8} className="me-1" />
                                      Inscripción abierta
                                    </span>
                                  </div>
                                  <div className="diplomado-image-frame">
                                    <DiplomadoImage
                                      basepath={diplomado.basepath}
                                      fileImage={diplomado.fileImage}
                                      alt={diplomado.nombre}
                                      className="diplomado-image-enhanced"
                                    />
                                  </div>
                                </div>
                              </CCol>

                              <CCol lg={8} className="diplomado-content-section">
                                <div className="content-wrapper">
                                  <div className="content-header">
                                    <CTooltip content={diplomado.nombre} placement="top">
                                      <div style={{ display: 'inline-block', width: '100%' }}>
                                        <h2 className="fw-bold text-primary" style={{ fontSize: '1.25rem', cursor: 'help' }}>
                                          {diplomado.nombre}
                                        </h2>
                                      </div>
                                    </CTooltip>
                                    {diplomado.descripcion && (
                                      <CTooltip content={diplomado.descripcion} placement="top">
                                        <div style={{ display: 'inline-block', width: '100%' }}>
                                          <p className="text-muted descripcion-text" style={{ fontSize: '0.8125rem', cursor: 'help' }}>
                                            {diplomado.descripcion}
                                          </p>
                                        </div>
                                      </CTooltip>
                                    )}
                                  </div>

                                  {diplomado.explicacion && (
                                    <div className="info-card p-2 rounded-3" style={{ background: 'linear-gradient(135deg, #fff9e6 0%, #fffef8 100%)', border: '1px solid #ffe58f' }}>
                                      <div className="d-flex align-items-start gap-2">
                                        <div className="icon-wrapper">
                                          <div className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px', minWidth: '28px' }}>
                                            <Icon icono="mdiLightbulbOnOutline" color="warning" size={0.9} />
                                          </div>
                                        </div>
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                          <h6 className="fw-semibold text-dark">¿Qué aprenderás?</h6>
                                          <CTooltip content={diplomado.explicacion} placement="top">
                                            <div style={{ display: 'inline-block', width: '100%' }}>
                                              <p className="text-muted" style={{ cursor: 'help' }}>{diplomado.explicacion}</p>
                                            </div>
                                          </CTooltip>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {diplomado.requisitos && (
                                    <div className="info-card p-2 rounded-3" style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #f6fcff 100%)', border: '1px solid #91d5ff' }}>
                                      <div className="d-flex align-items-start gap-2">
                                        <div className="icon-wrapper">
                                          <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px', minWidth: '28px' }}>
                                            <Icon icono="mdiClipboardListOutline" color="info" size={0.9} />
                                          </div>
                                        </div>
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                          <h6 className="fw-semibold text-dark">Requisitos</h6>
                                          <CTooltip content={diplomado.requisitos} placement="top">
                                            <div style={{ display: 'inline-block', width: '100%' }}>
                                              <p className="text-muted" style={{ cursor: 'help' }}>{diplomado.requisitos}</p>
                                            </div>
                                          </CTooltip>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {(diplomado.edadMinima || diplomado.edadMaxima) && (
                                    <div className="info-badge d-inline-flex align-items-center gap-2 px-2 py-1 rounded-pill" style={{ background: 'rgba(var(--cui-primary-rgb), 0.1)', border: '1px solid rgba(var(--cui-primary-rgb), 0.2)' }}>
                                      <Icon icono="mdiAccountOutline" size={0.85} color="primary" />
                                      <span className="text-primary fw-semibold" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                        Edad: {diplomado.edadMinima || 0} - {diplomado.edadMaxima || '+'} años
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </CCol>
                            </CRow>
                          </div>
                        </CCarouselItem>
                      ))}
                    </CCarousel>
                  </CCardBody>

                  <div className="card-footer-custom p-2 bg-gradient-light">
                    <CRow className="align-items-center g-2">
                      <CCol md={7} className="text-center text-md-start">
                        <div className="d-flex align-items-center justify-content-center justify-content-md-start gap-2">
                          <div className="flex-shrink-0">
                            <Icon icono="mdiCheckDecagram" size={1} color="success" />
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <p className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.8125rem' }}>
                              {availableDiplomados.length === 1
                                ? 'Programa disponible'
                                : `${availableDiplomados.length} programas disponibles`}
                            </p>
                          </div>
                        </div>
                      </CCol>
                      <CCol md={5} className="text-center text-md-end">
                        <CustomButton
                          variant="primary"
                          color="primary"
                          className="px-3 py-2 shadow-sm w-100 w-md-auto"
                          onClick={() => setShowForm(true)}
                        >
                          <span style={{ fontSize: '0.8125rem' }}>Continuar al registro</span>
                          <Icon icono="mdiArrowRight" className="ms-1" size={0.85} />
                        </CustomButton>
                      </CCol>
                    </CRow>
                  </div>
                </CCard>
              </div>

              <CRow className="mt-2">
                <CCol className="text-center animate-fade-in-delay">
                  <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', fontSize: '0.8125rem' }}>
                    <span className="text-white">¿Ya tienes cuenta?</span>
                    <button
                      type="button"
                      className="btn-link-light fw-bold"
                      onClick={() => navigate(`${SERVER_ROUTE}/principal`)}
                    >
                      Inicia sesión aquí
                    </button>
                  </div>
                </CCol>
              </CRow>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    );
  }

  return (
    <div className="register-page min-vh-100 d-flex align-items-center py-5">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol lg={8} md={10}>
            <CCard className="register-card shadow-lg border-0 position-relative">
              <CCardBody className="p-4 p-lg-5">
                <div className="mb-3">
                  <button
                    type="button"
                    className="btn-link-custom"
                    onClick={() => setShowForm(false)}
                    disabled={loading || status === 'loading'}
                  >
                    <Icon icono="mdiArrowLeft" size={0.9} className="me-1" />
                    Volver a diplomados
                  </button>
                </div>

                <div className="text-center mb-4">
                  <h2 className="register-title fw-bold mb-2">Registro de aspirantes</h2>
                  <p className="register-subtitle text-muted">
                    Completa tu información personal. Recibirás tus credenciales de acceso por SMS para continuar desde la plataforma.
                  </p>
                </div>

                <CForm onSubmit={formik.handleSubmit} className="row g-3">
                  <CRow>
                    <CCol md={12} className="mb-3">
                      <CFormLabel htmlFor="numeroTelefono">Número de teléfono<span className="text-danger">*</span></CFormLabel>
                      <CFormInputWithMask
                        id="numeroTelefono"
                        placeholder="Ingresa tu número de teléfono"
                        value={formik.values.numeroTelefono}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.numeroTelefono && Boolean(formik.errors.numeroTelefono)}
                        feedbackInvalid={formik.errors.numeroTelefono as string}
                        readOnly={loading || status === 'loading'}
                        mask={'00000000'}
                      />
                      <small className="text-muted">
                        Recibirás tus credenciales de acceso por SMS a este número
                      </small>
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="primerNombre">Primer nombre<span className="text-danger">*</span></CFormLabel>
                      <CFormInput
                        id="primerNombre"
                        name="primerNombre"
                        placeholder="Ingresa tu primer nombre"
                        value={formik.values.primerNombre}
                        onChange={handleNameChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.primerNombre && Boolean(formik.errors.primerNombre)}
                        readOnly={loading || status === 'loading'}
                        maxLength={50}
                      />
                      <CFormFeedback invalid>{formik.errors.primerNombre as string}</CFormFeedback>
                    </CCol>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="segundoNombre">Segundo nombre</CFormLabel>
                      <CFormInput
                        id="segundoNombre"
                        name="segundoNombre"
                        placeholder="Ingresa tu segundo nombre"
                        value={formik.values.segundoNombre}
                        onChange={handleNameChange}
                        onBlur={formik.handleBlur}
                        readOnly={loading || status === 'loading'}
                        maxLength={50}
                      />
                      {formik.touched.segundoNombre && formik.errors.segundoNombre && (
                        <div className="invalid-feedback d-block mt-1">{formik.errors.segundoNombre as string}</div>
                      )}
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="tercerNombre">Tercer nombre</CFormLabel>
                      <CFormInput
                        id="tercerNombre"
                        name="tercerNombre"
                        placeholder="Ingresa tu tercer nombre"
                        value={formik.values.tercerNombre}
                        onChange={handleNameChange}
                        onBlur={formik.handleBlur}
                        readOnly={loading || status === 'loading'}
                        maxLength={50}
                      />
                      {formik.touched.tercerNombre && formik.errors.tercerNombre && (
                        <div className="invalid-feedback d-block mt-1">{formik.errors.tercerNombre as string}</div>
                      )}
                    </CCol>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="primerApellido">Primer apellido<span className="text-danger">*</span></CFormLabel>
                      <CFormInput
                        id="primerApellido"
                        name="primerApellido"
                        placeholder="Ingresa tu primer apellido"
                        value={formik.values.primerApellido}
                        onChange={handleNameChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.primerApellido && Boolean(formik.errors.primerApellido)}
                        readOnly={loading || status === 'loading'}
                        maxLength={50}
                      />
                      <CFormFeedback invalid>{formik.errors.primerApellido as string}</CFormFeedback>
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="segundoApellido">Segundo apellido</CFormLabel>
                      <CFormInput
                        id="segundoApellido"
                        name="segundoApellido"
                        placeholder="Ingresa tu segundo apellido"
                        value={formik.values.segundoApellido}
                        onChange={handleNameChange}
                        onBlur={formik.handleBlur}
                        readOnly={loading || status === 'loading'}
                        maxLength={50}
                      />
                      {formik.touched.segundoApellido && formik.errors.segundoApellido && (
                        <div className="invalid-feedback d-block mt-1">{formik.errors.segundoApellido as string}</div>
                      )}
                    </CCol>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="cui">CUI<span className="text-danger">*</span></CFormLabel>
                      <CFormInputWithMask
                        id="cui"
                        placeholder="Ingresa tu CUI (13 dígitos)"
                        value={formik.values.cui || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.cui && Boolean(formik.errors.cui)}
                        feedbackInvalid={formik.errors.cui as string}
                        readOnly={loading || status === 'loading'}
                        mask={'0000000000000'}
                      />
                    </CCol>
                  </CRow>

                  <CRow>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="idGenero">Género<span className="text-danger">*</span></CFormLabel>
                      <CFormSelect
                        id="idGenero"
                        value={formik.values.idGenero || ''}
                        onChange={(event) => formik.setFieldValue('idGenero', event.target.value ? Number(event.target.value) : '')}
                        onBlur={() => formik.setFieldTouched('idGenero', true)}
                        disabled={loading || status === 'loading'}
                        invalid={formik.touched.idGenero && Boolean(formik.errors.idGenero)}
                      >
                        <option value="">Selecciona una opción</option>
                        {generoOptions.map((genero: any) => (
                          <option key={genero.idGenero} value={genero.idGenero}>{genero.nombre}</option>
                        ))}
                      </CFormSelect>
                      <CFormFeedback invalid>{formik.errors.idGenero as string}</CFormFeedback>
                    </CCol>
                    <CCol md={6} className="mb-3">
                      <CFormLabel htmlFor="fechaNacimiento">Fecha de nacimiento<span className="text-danger">*</span></CFormLabel>
                      <InputDatePicker
                        value={formik.values.fechaNacimiento ? new Date(`${formik.values.fechaNacimiento}T00:00:00`) : null}
                        minDate={new Date(new Date().getFullYear() - 100, 0, 1)}
                        maxDate={new Date()}
                        onChange={(date: Date | null) =>
                          formik.setFieldValue('fechaNacimiento', date ? date.toISOString().split('T')[0] : null)
                        }
                        error={formik.touched.fechaNacimiento && Boolean(formik.errors.fechaNacimiento)}
                      />
                      {formik.touched.fechaNacimiento && formik.errors.fechaNacimiento && (
                        <div className="invalid-feedback d-block mt-1">{formik.errors.fechaNacimiento as string}</div>
                      )}
                    </CCol>
                  </CRow>

                  <CRow className="mt-2">
                    <CCol>
                      <CustomButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-100"
                        disabled={loading || status === 'loading'}
                      >
                        Crear cuenta
                      </CustomButton>
                    </CCol>
                  </CRow>

                  <CRow className="mt-3">
                    <CCol className="text-center">
                      <span className="text-muted">¿Ya tienes cuenta?</span>
                      <button
                        type="button"
                        className="btn-link-custom ms-2"
                        onClick={() => navigate(`${SERVER_ROUTE}/principal`)}
                        disabled={loading || status === 'loading'}
                      >
                        Inicia sesión
                      </button>
                    </CCol>
                  </CRow>
                </CForm>

                {(loading || status === 'loading') && (
                  <UnifiedLoader variant="minimal" size="md" className="position-absolute top-50 start-50 translate-middle" />
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
      <ToastBase
        autohide={false}
        visible={visible}
        color={color}
        message={message}
        data={data}
        title={title}
        onClose={() => { dispatch(setVisibleToast(false)); }}
      />
    </div>
  );
};

export default Registro;
